import { addDays, diffDays } from '@dairy/shared';
import { eventsOf } from './events';
import type { DomainAnimal, DomainEvent } from './types';

export interface Lactation {
  number: number;
  calvingId: string;
  calvingDate: string;
  /** Last milking day (dry-off date, or the day before the next calving); null while open. */
  endDate: string | null;
  endReason: 'DRY_OFF' | 'NEXT_CALVING' | null;
  isOpening: boolean;
}

/** D-3 Lactations, oldest first. */
export function deriveLactations(animal: DomainAnimal, events: readonly DomainEvent[]): Lactation[] {
  const evs = eventsOf(events, animal.id);
  const calvings = evs.filter((e) => e.type === 'CALVING');
  if (calvings.length === 0) return [];
  // The opening calving is lactation `openingParity`; animals without opening history start at 1.
  const first = calvings[0]!;
  const base = first.isOpening && animal.openingParity > 0 ? animal.openingParity : animal.openingParity + 1;
  return calvings.map((c, i) => {
    const next = calvings[i + 1];
    const dryOff = evs.find(
      (e) => e.type === 'DRY_OFF' && e.date >= c.date && (!next || e.date < next.date),
    );
    let endDate: string | null = null;
    let endReason: Lactation['endReason'] = null;
    if (dryOff) {
      endDate = dryOff.date;
      endReason = 'DRY_OFF';
    } else if (next) {
      endDate = addDays(next.date, -1);
      endReason = 'NEXT_CALVING';
    }
    return {
      number: base + i,
      calvingId: c.id,
      calvingDate: c.date,
      endDate,
      endReason,
      isOpening: !!c.isOpening,
    };
  });
}

export interface CurrentLactation {
  number: number;
  calvingId: string;
  calvingDate: string;
  dim: number;
  /** false when the cow is dry on `date` */
  open: boolean;
  endDate: string | null;
}

/** D-3: lactation that started with the latest calving on or before `date`. */
export function currentLactation(
  animal: DomainAnimal,
  events: readonly DomainEvent[],
  date: string,
): CurrentLactation | null {
  const lacts = deriveLactations(animal, events).filter((l) => l.calvingDate <= date);
  const l = lacts[lacts.length - 1];
  if (!l) return null;
  return {
    number: l.number,
    calvingId: l.calvingId,
    calvingDate: l.calvingDate,
    dim: diffDays(date, l.calvingDate),
    open: !(l.endDate && l.endDate < date),
    endDate: l.endDate,
  };
}

/** Parity on `date`: number of the latest lactation, or the opening parity when no calving is recorded. */
export function parityOn(animal: DomainAnimal, events: readonly DomainEvent[], date: string): number {
  return currentLactation(animal, events, date)?.number ?? animal.openingParity;
}

/** DIM on `date` (calving day = 0), or null when the cow has not calved. */
export function dimOn(animal: DomainAnimal, events: readonly DomainEvent[], date: string): number | null {
  return currentLactation(animal, events, date)?.dim ?? null;
}
